import Vue from 'vue'

import { get } from 'lodash'

import { isAbortRequestError } from '@sigma-frontend-libs/commons'

import { getTranslate } from './getters/getTranslate'
import { globalErrorHandler, showErrorMessages } from './request/error'

const messageLengthLimit = 500
const stackLengthLimit = 1000

/**
 * @typedef {object} CaughtError
 * @property {string} origin Источник перехвата: `component`, `window` или `rejection`.
 * @property {string} message Сообщение ошибки.
 * @property {string} [stack] Первая строка стека.
 * @property {string} [component] Имя компонента Vue.
 * @property {string} [info] Стадия жизненного цикла Vue, на которой возникла ошибка.
 * @property {string} [file] Файл, в котором возникла ошибка.
 * @property {number} [line] Строка, в которой возникла ошибка.
 * @property {number} [column] Колонка, в которой возникла ошибка.
 * @property {*} raw Исходное перехваченное значение.
 */

/**
 * Потребители перехваченных ошибок.
 *
 * @type {Array<function(CaughtError): void>}
 */
const caughtErrorConsumers = [ showCaughtError ]

/**
 * Обрезает строку до заданной длины.
 *
 * @param {string} value Исходная строка.
 * @param {number} limit Предельная длина результата.
 * @returns {string} Строка длиной не больше `limit`.
 */
function truncateText(value, limit) {
  if (value.length <= limit) {
    return value
  }

  return value.slice(0, limit)
}

/**
 * Возвращает сообщение перехваченного значения.
 *
 * @param {*} source Перехваченное значение.
 * @returns {string} Сообщение, обрезанное до предельной длины.
 */
function getCaughtMessage(source) {
  if (typeof source === 'string') {
    return truncateText(source, messageLengthLimit)
  }

  const message = get(source, 'message')
  if (typeof message === 'string' && message) {
    return truncateText(message, messageLengthLimit)
  }

  return truncateText(String(source), messageLengthLimit)
}

/**
 * Возвращает первую строку стека перехваченного значения.
 *
 * @param {*} source Перехваченное значение.
 * @returns {string|undefined} Первая строка стека или `undefined`, если стека нет.
 */
function getFirstStackLine(source) {
  const stack = get(source, 'stack')
  if (typeof stack !== 'string' || !stack) {
    return undefined
  }

  const [ firstLine ] = stack.split('\n')

  return truncateText(firstLine.trim(), stackLengthLimit)
}

/**
 * Определяет, является ли перехваченное значение ошибкой запроса.
 *
 * Ошибка запроса приходит от `errorRequest` обычным объектом с числовым кодом
 * ответа, ошибка выполнения кода приходит экземпляром `Error`.
 *
 * @param {*} source Перехваченное значение.
 * @returns {boolean} Признак ошибки запроса.
 */
function isRequestError(source) {
  if (!source || typeof source !== 'object' || source instanceof Error) {
    return false
  }

  return typeof source.error === 'number' || typeof source.code === 'number'
}

/**
 * Определяет, относится ли перехваченное значение к отменённому запросу.
 *
 * @param {*} source Перехваченное значение.
 * @returns {boolean} Признак отменённого запроса.
 */
function isAbortedRequest(source) {
  if (!source || typeof source !== 'object') {
    return false
  }

  return isAbortRequestError(source)
}

/**
 * Ограничивает длину строковых сведений источника.
 *
 * @param {object} details Дополнительные сведения источника.
 * @returns {object} Сведения, в которых каждая строка не длиннее предела сообщения.
 */
function truncateDetails(details) {
  const result = {}
  for (const key of Object.keys(details)) {
    const value = details[key]
    if (typeof value === 'string') {
      result[key] = truncateText(value, messageLengthLimit)
      continue
    }

    result[key] = value
  }

  return result
}

/**
 * Собирает описание перехваченной ошибки.
 *
 * Сообщение и строковые сведения источника ограничены 500 символами,
 * первая строка стека - 1000 символами. Исходное значение сохраняется
 * в `raw` без изменений.
 *
 * @param {string} origin Источник перехвата.
 * @param {*} source Перехваченное значение.
 * @param {object} [details] Дополнительные сведения источника.
 * @returns {CaughtError} Описание перехваченной ошибки.
 */
export function createCaughtError(origin, source, details = {}) {
  return {
    origin,
    message: getCaughtMessage(source),
    stack: getFirstStackLine(source),
    raw: source,
    ...truncateDetails(details)
  }
}

/**
 * Собирает описание перехваченной ошибки без исходного значения.
 *
 * Все остальные поля ограничены по длине в `createCaughtError`, а исходное
 * значение не ограничено и в вывод не попадает.
 *
 * @param {CaughtError} caughtError Описание перехваченной ошибки.
 * @returns {object} Описание для вывода в консоль.
 */
function getPrintableCaughtError(caughtError) {
  const result = {}
  for (const key of Object.keys(caughtError)) {
    if (key !== 'raw') {
      result[key] = caughtError[key]
    }
  }

  return result
}

/**
 * Показывает перехваченную ошибку пользователю и пишет её в консоль.
 *
 * В консоль попадают только поля с ограниченной длиной. Ошибка запроса
 * передаётся в `globalErrorHandler`, ошибка выполнения кода показывается
 * снекбаром с текстом неизвестной ошибки.
 *
 * @param {CaughtError} caughtError Описание перехваченной ошибки.
 */
function showCaughtError(caughtError) {
  console.error('[caught]', getPrintableCaughtError(caughtError))

  if (isRequestError(caughtError.raw)) {
    globalErrorHandler(caughtError.raw)

    return
  }

  showErrorMessages([
    {
      type: 'snackbar',
      translate: getTranslate('errors.unknown.type')
    }
  ])
}

/**
 * Передаёт описание перехваченной ошибки всем потребителям.
 *
 * Ошибка самого потребителя не прерывает обход и пишется в консоль.
 *
 * @param {CaughtError} caughtError Описание перехваченной ошибки.
 */
export function handleCaughtError(caughtError) {
  for (const consumer of caughtErrorConsumers) {
    try {
      consumer(caughtError)
    } catch (consumerError) {
      console.error(consumerError)
    }
  }
}

/**
 * Добавляет потребителя перехваченных ошибок.
 *
 * @param {function(CaughtError): void} consumer Потребитель перехваченного.
 */
export function addCaughtErrorConsumer(consumer) {
  if (typeof consumer === 'function') {
    caughtErrorConsumers.push(consumer)
  }
}

/**
 * Возвращает имя компонента Vue.
 *
 * @param {object} vm Экземпляр компонента Vue.
 * @returns {string|undefined} Имя компонента или `undefined`, если имя не задано.
 */
function getComponentName(vm) {
  return get(vm, '$options.name') ?? get(vm, '$options._componentTag')
}

/**
 * Подключает перехват ошибок компонентов Vue, ошибок окна и необработанных
 * отклонений промисов.
 *
 * Ранее установленные обработчики сохраняются и получают управление после
 * обработки перехваченного.
 */
export function registerErrorInterception() {
  const previousComponentErrorHandler = Vue.config.errorHandler
  Vue.config.errorHandler = (error, vm, info) => {
    handleCaughtError(createCaughtError('component', error, {
      component: getComponentName(vm),
      info
    }))

    if (typeof previousComponentErrorHandler === 'function') {
      previousComponentErrorHandler(error, vm, info)
    }
  }

  const previousWindowErrorHandler = window.onerror
  window.onerror = (message, file, line, column, error) => {
    handleCaughtError(createCaughtError('window', error ?? message, {
      file,
      line,
      column
    }))

    if (typeof previousWindowErrorHandler === 'function') {
      return previousWindowErrorHandler(message, file, line, column, error)
    }

    return false
  }

  window.addEventListener('unhandledrejection', event => {
    if (isAbortedRequest(event.reason)) {
      return
    }

    handleCaughtError(createCaughtError('rejection', event.reason))
  })
}
